import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Form, { type FormHandle } from "../../../Form/Form";
import Input from "../../../Form/Input/Input";
import DefaultButton from "../../../../components/simple/DefaultButton";
import DragDropFileUpload from "../../../DragDropFileUpload/DragDropFileUpload";
import EmailChangeModal from "./EmailChangeModal";
import NumberChangeModal from "./NumberChangeModal";

import { Avatar } from "@mui/material";
import { PersonOutline } from "@mui/icons-material";

const Personal = ({
  user,
  setLoading,
  setError,
  setSuccess,
  brand,
  partialAuthToken,
  fetchUser,
}) => {
  const formRef = useRef<FormHandle>(null);
  const [previewImage, setPreviewImage] = useState(user?.profile_picture);
  const [pendingEmail, setPendingEmail] = useState(user?.email || "");
  const [pendingNumber, setPendingNumber] = useState(user?.mobile_number || "");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const [pendingEmailForModal, setPendingEmailForModal] = useState("");
  const [pendingNumberForModal, setPendingNumberForModal] = useState("");
  const [pendingConsent, setPendingConsent] = useState<boolean>(
    !!user?.has_given_2fa_consent,
  );
  const location = useLocation();

  const queryClient = useQueryClient();

  useEffect(() => {
    setPendingEmail(user?.email || "");
    setPendingNumber(user?.mobile_number || "");
  }, [user?.email, user?.mobile_number]);

  const fields = [
    {
      id: "firstName",
      label: "Name",
      fields: [
        {
          id: "first_name",
          name: "first_name",
          type: "text",
          placeholder: "First Name",
        },
        {
          id: "last_name",
          name: "last_name",
          type: "text",
          placeholder: "Last Name",
        },
      ],
    },
    {
      id: "job_role",
      label: "Job Role",
      fields: [
        {
          id: "job_role",
          name: "job_role",
          type: "text",
          placeholder: "Job Role",
        },
      ],
    },
  ];

  const handleError = (err) => {
    setSuccess(false);
    setError(err);
    setLoading(false);
  };

  const handleSuccess = () => {
    setLoading(false);
    setSuccess(true);
    queryClient.invalidateQueries({ queryKey: ["user"] });

    const emailChanged = pendingEmail.trim() !== (user?.email || "");
    const numberChanged = pendingNumber.trim() !== (user?.mobile_number || "");

    if (emailChanged) {
      setPendingEmailForModal(pendingEmail.trim());
      if (numberChanged && pendingConsent) {
        setPendingNumberForModal(pendingNumber.trim());
      }
      setEmailModalOpen(true);
    } else if (numberChanged && pendingConsent) {
      setPendingNumberForModal(pendingNumber.trim());
      setNumberModalOpen(true);
    }
  };

  const handleFilesSelected = (
    files,
    setUploadProgress,
    setError,
    setSuccess,
  ) => {
    files.forEach((file, index) => {
      const formData = new FormData();
      formData.append("profile_picture", file);

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) =>
            prev.map((item, i) => (i === index ? { ...item, progress } : item)),
          );
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.response);
          setSuccess(true);
          setPreviewImage(response.profile_picture);
          fetchUser();
        } else {
          setError(`Upload failed for ${file.name}: ${xhr.statusText}`);
        }
      };

      xhr.onerror = () => {
        setError(`Upload failed for ${file.name}: Network error`);
      };

      xhr.open("PATCH", `${brand.api_base_url}/user/${user.id}/`, true);
      // Auth rides on the HttpOnly cookie (set by the backend); withCredentials
      // makes the browser attach it on this cross-origin upload. There is no
      // JS-readable token to put in an Authorization header anymore.
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  };

  console.log({ user });

  return (
    <div className="profile-fields-container">
      <Form
        ref={formRef}
        action={`user/${user.id}/`}
        method="PATCH"
        onError={handleError}
        onBefore={() => setLoading(true)}
        onSuccess={handleSuccess}
        auth
        options={{
          defaultValues: {
            has_given_2fa_consent: user?.has_given_2fa_consent,
            email: pendingEmail || "",
            mobile_number: pendingNumber || "",
          },
        }}
        transformValues={({ email, mobile_number, ...rest }) => rest}
      >
        {({ register, formState: { errors }, control, watch }) => {
          const watchedConsent = watch("has_given_2fa_consent");
          const watchedEmail = watch("email");
          const watchedMobile = watch("mobile_number");

          useEffect(() => {
            setPendingConsent(!!watchedConsent);
          }, [watchedConsent]);

          useEffect(() => {
            if (watchedEmail !== undefined) {
              setPendingEmail(watchedEmail);
            }
          }, [watchedEmail]);

          useEffect(() => {
            if (watchedMobile !== undefined) {
              setPendingNumber(watchedMobile);
            }
          }, [watchedMobile]);

          return (
            <>
              {fields.map((section, sectionIndex) => (
                <div key={sectionIndex} className="form-section">
                  <label htmlFor={section.id}>{section.label}</label>
                  <div className="field-group">
                    {section.fields.map((field, fieldIndex) => (
                      <Input
                        id={field.name}
                        label={field.label}
                        showLabel={false}
                        key={fieldIndex}
                        type={field.type || "text"}
                        placeHolder={field.placeholder}
                        register={register(field.name, {
                          required: "Field is required",
                        })}
                        error={errors[field.name]?.message}
                        control={control}
                        value={user[field.id]}
                        helpText={field.helpText}
                      />
                    ))}
                  </div>
                </div>
              ))}
              <div className="form-section">
                <label>Email Address</label>
                <div className="field-group">
                  <Input
                    id="email"
                    showLabel={false}
                    label="Email Address"
                    type="email"
                    placeHolder="email@example.com"
                    register={register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    error={errors["email"]?.message}
                    control={control}
                    helpText="If you change this, you will need to verify the new email address"
                  />
                </div>
              </div>
              <div className="form-section">
                <label>Phone Number</label>
                <div className="field-group">
                  <Input
                    id="mobile_number"
                    showLabel={false}
                    label="Phone Number"
                    type="mobile"
                    placeHolder="Mobile number"
                    register={register("mobile_number", {
                      required: "Phone number is required",
                    })}
                    error={errors["mobile_number"]?.message}
                    control={control}
                    helpText="We use your mobile for two-step verification (2FA) to keep your account secure. Changing it will ask you to reverify if you have 2FA enabled"
                  />
                </div>
              </div>
              <div className="form-section">
                <label htmlFor="has_given_2fa_consent">
                  Two-Factor Authentication
                </label>
                <div className="field-group">
                  <Input
                    id="has_given_2fa_consent"
                    showLabel={false}
                    type="checkbox"
                    label="I agree to GuruNow sending me a verification code every time I log in. Message and data rates may apply."
                    register={register("has_given_2fa_consent")}
                    error={errors["has_given_2fa_consent"]?.message}
                    control={control}
                  />
                </div>
              </div>
              <div className="form-section">
                <label>
                  Your Photo{" "}
                  <span>This will be displayed on your profile.</span>
                </label>
                <div className="field-group">
                  <div className="avatar-container">
                    {user && user.profile_picture ? (
                      <img
                        src={previewImage}
                        alt="Profile"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          display: "flex",
                          position: "relative",
                          mx: "auto",
                          alignItems: "center",
                          backgroundColor: "#01ECFF",
                          color: "#02002B",
                          cursor: "pointer",
                          width: 50,
                          height: 50,
                          margin: 0,
                        }}
                      >
                        <PersonOutline />
                      </Avatar>
                    )}
                    <DragDropFileUpload
                      onFilesSelected={handleFilesSelected}
                      maxFiles={1}
                      accept="image/jpeg,image/png,image/gif,image/svg+xml"
                      maxSize={2 * 1024 * 1024}
                    />
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <DefaultButton buttonSize="md" wideOnMobile type="submit">
                  Save Changes
                </DefaultButton>
              </div>
            </>
          );
        }}
      </Form>
      <EmailChangeModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSuccess={() => {
          setEmailModalOpen(false);
          setSuccess(true);
          queryClient.invalidateQueries({ queryKey: ["user"] });
          if (pendingNumberForModal) {
            setNumberModalOpen(true);
          }
        }}
        currentEmail={user?.email || ""}
        prefilledNewEmail={pendingEmailForModal}
      />
      <NumberChangeModal
        open={numberModalOpen}
        onClose={() => setNumberModalOpen(false)}
        onSuccess={() => {
          setNumberModalOpen(false);
          setPendingNumberForModal("");
          setSuccess(true);
          queryClient.invalidateQueries({ queryKey: ["user"] });
        }}
        currentNumber={user?.mobile_number || ""}
        prefilledNewNumber={pendingNumberForModal}
        twofaConsent={pendingConsent}
      />
    </div>
  );
};

export default Personal;
